import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Clock, ArrowRight } from 'lucide-react';
import type { Article } from '../../types/blog';
import { SILOS } from '../../data/blog/silos';
import SiloBadge from './SiloBadge';

interface ArticleCardProps {
  article: Article;
}

const ArticleCard: React.FC<ArticleCardProps> = ({ article }) => {
  const silo = SILOS[article.silo];
  const coverImage = article.coverImage ?? '/images/blog-hero.png';
  const coverAlt = article.coverImageAlt ?? `Illustration de l'article : ${article.h1}`;

  return (
    <motion.div
      className="group relative flex flex-col overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-b from-white/10 to-white/[0.03] backdrop-blur-sm h-full"
      whileHover={{ y: -6, scale: 1.01, transition: { duration: 0.22, ease: 'easeOut' } }}
    >
      <div className={`h-2 w-full ${silo.color}`} />
      <div className="relative aspect-[16/9] overflow-hidden border-b border-white/10">
        <img
          src={coverImage}
          alt={coverAlt}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
        />
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#2F3136]/60 via-transparent to-transparent" />
      </div>

      <div className="flex flex-col flex-1 p-5 gap-3">
        <div className="flex items-center justify-between gap-2">
          <SiloBadge silo={article.silo} />
          {article.isPillar && (
            <span className="rounded-full border border-[#5865F2]/35 bg-[#5865F2]/15 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider text-[#B9C4FF]">
              Pilier
            </span>
          )}
        </div>

        <h2 className="text-white font-semibold text-base leading-snug group-hover:text-[#B9C4FF] transition-colors line-clamp-2">
          {article.h1}
        </h2>

        <p className="text-gray-400 text-sm leading-relaxed line-clamp-3 flex-1">
          {article.excerpt}
        </p>

        <div className="flex items-center justify-between mt-auto pt-3 border-t border-white/10">
          <span className="flex items-center gap-1.5 text-gray-500 text-xs">
            <Clock className="h-3.5 w-3.5" />
            {article.readingMinutes} min de lecture
          </span>
          <Link
            to={`/blog/${article.slug}`}
            className="inline-flex items-center gap-1.5 text-[#5865F2] hover:text-[#B9C4FF] text-sm font-medium transition-colors"
          >
            Lire l'article <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      </div>
    </motion.div>
  );
};

export default ArticleCard;
